<template>
  <div class="d-flex align-center">
    <v-select
      :value="selectedValue"
      :items="items"
      :label="label"
      hide-details
      dense
      outlined
      :disabled="disabled"
      data-cy="slack-channel"
      @change="onSelect"
    />
    <v-btn icon small class="ml-1" title="how to set up a Slack channel" @click="showHelp = true" data-cy="slack-help">
      <v-icon small>mdi-help-circle-outline</v-icon>
    </v-btn>

    <!-- a channel that no macro uses yet: name for people, id for slack -->
    <v-dialog v-model="showOther" max-width="480" @keydown.enter="confirmOther">
      <v-card>
        <v-card-title class="subtitle-1">Slack channel</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="other.name"
            label="Channel name"
            placeholder="#automated-printing"
            hint="shown in the kitchen"
            persistent-hint
            autofocus
            data-cy="slack-channel-name"
          />
          <v-text-field
            v-model="other.id"
            label="Channel id"
            placeholder="C0C1TT6KGPM"
            hint="from the channel's details in Slack (see the help)"
            persistent-hint
            class="mt-2"
            data-cy="slack-channel-id"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn text small @click="showOther = false; showHelp = true">How do I find these?</v-btn>
          <v-spacer/>
          <v-btn text @click="showOther = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!other.id.trim()" @click="confirmOther" data-cy="slack-channel-ok">Use channel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showHelp" max-width="640">
      <v-card>
        <v-card-title class="subtitle-1">Posting pdfs to Slack</v-card-title>
        <v-card-text>
          <p>
            <em>Pdfs go to</em> says whether a server render's pdfs download here or are posted to Slack.
            With <em>Post to Slack</em> the server posts every pdf of a render to the macro's channel as one
            message when the render finishes and keeps no copy; with <em>Download here</em> the pdfs download
            as they finish and stay on the server for a week (the <em>Send to Slack</em> button posts them by
            hand). The channel is kept on the macro either way, so switching back and forth does not lose it.
            The api posts as a Slack app (a bot with the <code>files:write</code> permission) that the api
            server is configured with.
          </p>
          <h4 class="mt-3">1. Invite the app to the channel</h4>
          <p>
            The bot can only post to channels it is a member of. In Slack open the channel and type
            <code>/invite @&lt;app name&gt;</code>, or open the channel's details (click the channel name at
            the top), go to <em>Integrations</em>, <em>Add apps</em>, and add the app. Private channels
            work the same way once the app is a member.
          </p>
          <h4 class="mt-3">2. Find the channel id</h4>
          <p>
            Click the channel name at the top of the channel to open its details; the id is at the bottom of
            the <em>About</em> tab, e.g. <code>C0C1TT6KGPM</code> (there is a copy button next to it). It is
            also the last part of the channel's address in a browser
            (<code>app.slack.com/client/T.../C0C1TT6KGPM</code>). The name is only what the kitchen shows,
            so write it the way people know it, e.g. <code>#automated-printing</code>.
          </p>
          <h4 class="mt-3">3. Choose it on the macro</h4>
          <p>
            Pick a channel another macro already uses, or <em>Other channel...</em> and enter the name and
            id. The choice is saved with the macro. With no channel a slack delivery uses the api's default
            channel, if it has one.
          </p>
          <p class="grey--text mb-0">
            If a post fails with <code>not_in_channel</code> the app has not been invited yet; with
            <code>channel_not_found</code> the id is wrong (a private channel the app is not in looks the
            same).
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showHelp = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * Picks the slack channel a macro posts its pdfs to: none, a channel some macro
 * already names (so the id is typed once), or a new one by name and id. Carries the
 * explanation of the slack side (invite the app, find the id).
 */
const NONE = '';
const OTHER = '__other__';

export default {
  name: 'SlackChannelPicker',
  props: {
    // {name, id} or null
    value: {type: Object, default: null},
    // channels other macros use, [{name, id}]
    channels: {type: Array, default: () => []},
    label: {type: String, default: 'Slack channel'},
    disabled: {type: Boolean, default: false},
  },
  data() {
    return {
      showOther: false,
      showHelp: false,
      other: {name: '', id: ''},
    };
  },
  computed: {
    selectedValue() {
      return this.value && this.value.id ? this.value.id : NONE;
    },
    /** the known channels plus the current one if no other macro has it */
    items() {
      const known = new Map();
      [...this.channels, this.value].filter(c => c && c.id).forEach((c) => {
        if (!known.has(c.id)) {
          known.set(c.id, c);
        }
      });
      return [
        {text: 'none', value: NONE},
        ...[...known.values()].sort((a, b) => a.name.localeCompare(b.name)).map(c => ({text: c.name, value: c.id})),
        {text: 'Other channel...', value: OTHER},
      ];
    },
  },
  methods: {
    onSelect(id) {
      if (id === OTHER) {
        this.other = {name: '', id: ''};
        this.showOther = true;
        return;
      }
      const channel = this.items.find(i => i.value === id);
      this.$emit('input', id && channel ? {name: channel.text, id} : null);
    },
    confirmOther() {
      const id = this.other.id.trim();
      if (!id) {
        return;
      }
      this.showOther = false;
      this.$emit('input', {name: this.other.name.trim() || id, id});
    },
  },
};
</script>
